// /src/components/admin/LocationDepthAdmin.jsx
// Location Depth Admin Tab - Manage administrative level activation thresholds
// Allows admins to configure when deeper admin levels become available based on user penetration

import React, { useState, useEffect } from 'react';
import { supabaseFetch } from '../../lib/supabase';
import { useTheme } from '../../themes/ThemeProvider';
import { useTranslation } from '../../i18n/useTranslation';
import { Info } from 'lucide-react';
import FeedRankingSection from './FeedRankingSection';

const LocationDepthAdmin = ({ currentUser }) => {
  const theme = useTheme();
  const { t, formattingLocale } = useTranslation('admin');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // State
  const [globalThreshold, setGlobalThreshold] = useState('0.5');
  const [countries, setCountries] = useState([]);
  const [levels, setLevels] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [importedCountries, setImportedCountries] = useState([]);

  // Translation helper - returns fallback until translation system is ready
  const tAdmin = (key, fallback) => fallback;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load global threshold
      const { data: settings, error: settingsError } = await supabaseFetch.query('platform_settings', {
        select: '*',
        eq: { key: 'location_depth_default_threshold' },
        single: true
      });
      if (settingsError) throw settingsError;
      if (settings) {
        setGlobalThreshold(settings.value);
      }

      // Load available countries from get_available_countries RPC
      const { data: countriesData, error: countriesError } = await supabaseFetch.rpc('get_available_countries');
      if (countriesError) throw countriesError;
      setCountries(countriesData || []);

      // Auto-select first country if available
      if (countriesData && countriesData.length > 0) {
        setSelectedCountry(countriesData[0].country_code);
        await loadCountryLevels(countriesData[0].country_code);
      }

      // Load imported countries summary for info panel
      const { data: importSummary, error: importError } = await supabaseFetch.query('admin_boundaries', {
        select: 'country_code, country, admin_level'
      });
      if (importError) throw importError;

      // Group by country
      const countryMap = {};
      (importSummary || []).forEach(row => {
        if (!countryMap[row.country_code]) {
          countryMap[row.country_code] = {
            code: row.country_code,
            name: row.country,
            levels: new Set(),
            boundaries: 0
          };
        }
        countryMap[row.country_code].levels.add(row.admin_level);
        countryMap[row.country_code].boundaries++;
      });

      const importedList = Object.values(countryMap).map(c => ({
        code: c.code,
        name: c.name,
        levels: c.levels.size,
        boundaries: c.boundaries
      }));
      setImportedCountries(importedList);

    } catch (err) {
      console.error('Error loading location depth data:', err);
      setError(tAdmin('error.load_data', `Error loading data: ${err.message}`));
    } finally {
      setLoading(false);
    }
  };

  const loadCountryLevels = async (countryCode) => {
    try {
      const { data, error } = await supabaseFetch.rpc('get_available_levels', {
        p_country_code: countryCode
      });
      if (error) throw error;
      setLevels(data || []);
    } catch (err) {
      console.error('Error loading country levels:', err);
      setError(tAdmin('error.load_levels', `Error loading levels: ${err.message}`));
    }
  };

  const handleGlobalThresholdSave = async () => {
    try {
      setError(null);
      setSuccess(null);

      const { error } = await supabaseFetch.update('platform_settings', {
        value: globalThreshold,
        updated_at: new Date().toISOString(),
        updated_by: currentUser.id
      }, {
        eq: { key: 'location_depth_default_threshold' }
      });

      if (error) throw error;

      setSuccess(tAdmin('success.global_threshold', 'Global threshold updated successfully'));
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating global threshold:', err);
      setError(tAdmin('error.save_threshold', `Error saving threshold: ${err.message}`));
    }
  };

  const handleLevelToggle = async (level) => {
    try {
      setError(null);
      setSuccess(null);

      const { error } = await supabaseFetch.update('location_level_config', {
        is_active: !level.is_active,
        updated_at: new Date().toISOString()
      }, {
        eq: {
          country_code: selectedCountry,
          admin_level: level.admin_level
        }
      });

      if (error) throw error;

      // Reload levels
      await loadCountryLevels(selectedCountry);

      setSuccess(tAdmin('success.level_toggle', `Level ${level.level_label} ${level.is_active ? 'deactivated' : 'activated'}`));
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error toggling level:', err);
      setError(tAdmin('error.toggle_level', `Error toggling level: ${err.message}`));
    }
  };

  const handleCountrySelect = async (countryCode) => {
    setSelectedCountry(countryCode);
    await loadCountryLevels(countryCode);
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        {tAdmin('status.loading', 'Loading...')}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{
          margin: '0 0 8px 0',
          fontSize: '24px',
          fontWeight: 'bold',
          color: theme.text.primary
        }}>
          {tAdmin('title.location_depth', 'Location Depth Configuration')}
        </h2>
        <p style={{
          margin: 0,
          fontSize: '14px',
          color: theme.text.secondary,
          lineHeight: '1.5'
        }}>
          {tAdmin('subtitle.location_depth', 'Configure when administrative levels become available for user selection based on platform penetration.')}
        </p>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div style={{
          padding: '12px 16px',
          background: '#FEE2E2',
          border: '1px solid #FCA5A5',
          borderRadius: '6px',
          color: '#DC2626',
          marginBottom: '16px',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{
          padding: '12px 16px',
          background: '#D1FAE5',
          border: '1px solid #6EE7B7',
          borderRadius: '6px',
          color: '#059669',
          marginBottom: '16px',
          fontSize: '14px'
        }}>
          {success}
        </div>
      )}

      {/* Global Default Threshold */}
      <div style={{
        background: 'white',
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '24px'
      }}>
        <h3 style={{
          margin: '0 0 12px 0',
          fontSize: '18px',
          fontWeight: '600',
          color: theme.text.primary
        }}>
          {tAdmin('label.global_threshold', 'Global Default Threshold')}
        </h3>
        <p style={{
          margin: '0 0 16px 0',
          fontSize: '14px',
          color: theme.text.secondary,
          lineHeight: '1.5'
        }}>
          {tAdmin('help.global_threshold', 'Default percentage of country population required to activate the next administrative level. Used when no country-specific override is set.')}
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <label style={{
            fontSize: '14px',
            fontWeight: '500',
            color: theme.text.primary
          }}>
            {tAdmin('label.threshold_percent', 'Threshold (%)')}
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            max="100"
            value={globalThreshold}
            onChange={(e) => setGlobalThreshold(e.target.value)}
            style={{
              width: '120px',
              padding: '8px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
          <span style={{ fontSize: '14px', color: theme.text.secondary }}>
            {tAdmin('label.current_default', 'Current default')}: {globalThreshold}%
          </span>
          <button
            onClick={handleGlobalThresholdSave}
            style={{
              padding: '8px 16px',
              background: theme.primary,
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            {tAdmin('button.save', 'Save')}
          </button>
        </div>
      </div>

      {/* Country Selection */}
      {countries.length > 0 && (
        <div style={{
          background: 'white',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <h3 style={{
            margin: '0 0 12px 0',
            fontSize: '18px',
            fontWeight: '600',
            color: theme.text.primary
          }}>
            {tAdmin('label.country_config', 'Country Configuration')}
          </h3>
          <p style={{
            margin: '0 0 16px 0',
            fontSize: '14px',
            color: theme.text.secondary,
            lineHeight: '1.5'
          }}>
            {tAdmin('help.country_config', 'Select a country to view and configure its administrative level settings.')}
          </p>

          {/* Country Tabs */}
          <div style={{
            display: 'flex',
            gap: '8px',
            borderBottom: '2px solid #E5E7EB',
            marginBottom: '20px'
          }}>
            {countries.map(country => (
              <button
                key={country.country_code}
                onClick={() => handleCountrySelect(country.country_code)}
                style={{
                  padding: '10px 16px',
                  background: selectedCountry === country.country_code ? theme.primary : 'transparent',
                  color: selectedCountry === country.country_code ? 'white' : theme.text.secondary,
                  border: 'none',
                  borderBottom: selectedCountry === country.country_code ? `3px solid ${theme.primary}` : '3px solid transparent',
                  borderRadius: '4px 4px 0 0',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  marginBottom: '-2px'
                }}
              >
                {country.country_name} ({country.country_code})
              </button>
            ))}
          </div>

          {/* Level Configuration Table */}
          {selectedCountry && levels.length > 0 && (
            <div style={{
              border: '1px solid #E5E7EB',
              borderRadius: '6px',
              overflow: 'hidden'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#F9FAFB' }}>
                    <th style={{
                      padding: '12px 16px',
                      textAlign: 'left',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: theme.text.secondary,
                      borderBottom: '1px solid #E5E7EB'
                    }}>
                      {tAdmin('table.level', 'Level')}
                    </th>
                    <th style={{
                      padding: '12px 16px',
                      textAlign: 'left',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: theme.text.secondary,
                      borderBottom: '1px solid #E5E7EB'
                    }}>
                      {tAdmin('table.label', 'Label')}
                    </th>
                    <th style={{
                      padding: '12px 16px',
                      textAlign: 'center',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: theme.text.secondary,
                      borderBottom: '1px solid #E5E7EB'
                    }}>
                      {tAdmin('table.boundaries', 'Boundaries')}
                    </th>
                    <th style={{
                      padding: '12px 16px',
                      textAlign: 'center',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: theme.text.secondary,
                      borderBottom: '1px solid #E5E7EB'
                    }}>
                      {tAdmin('table.status', 'Status')}
                    </th>
                    <th style={{
                      padding: '12px 16px',
                      textAlign: 'center',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: theme.text.secondary,
                      borderBottom: '1px solid #E5E7EB'
                    }}>
                      {tAdmin('table.actions', 'Actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {levels.map(level => (
                    <tr key={level.admin_level} style={{
                      borderBottom: '1px solid #F3F4F6'
                    }}>
                      <td style={{
                        padding: '12px 16px',
                        fontSize: '14px',
                        color: theme.text.primary
                      }}>
                        {level.admin_level}
                      </td>
                      <td style={{
                        padding: '12px 16px',
                        fontSize: '14px',
                        color: theme.text.primary,
                        fontWeight: '500'
                      }}>
                        {level.level_label}
                        {level.level_label_pt && (
                          <span style={{ color: theme.text.secondary, fontSize: '12px', marginLeft: '8px' }}>
                            ({level.level_label_pt})
                          </span>
                        )}
                      </td>
                      <td style={{
                        padding: '12px 16px',
                        textAlign: 'center',
                        fontSize: '14px',
                        color: theme.text.secondary
                      }}>
                        {level.boundary_count.toLocaleString(formattingLocale)}
                      </td>
                      <td style={{
                        padding: '12px 16px',
                        textAlign: 'center'
                      }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: level.is_active ? '#D1FAE5' : '#FEE2E2',
                          color: level.is_active ? '#059669' : '#DC2626'
                        }}>
                          {level.is_active ? tAdmin('status.active', 'ACTIVE') : tAdmin('status.locked', 'LOCKED')}
                        </span>
                      </td>
                      <td style={{
                        padding: '12px 16px',
                        textAlign: 'center'
                      }}>
                        <button
                          onClick={() => handleLevelToggle(level)}
                          style={{
                            padding: '6px 12px',
                            background: level.is_active ? '#FEE2E2' : '#D1FAE5',
                            color: level.is_active ? '#DC2626' : '#059669',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '600'
                          }}
                        >
                          {level.is_active
                            ? tAdmin('button.deactivate', 'Deactivate')
                            : tAdmin('button.activate', 'Activate')
                          }
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Feed ranking settings, per country (Roadmap #104) */}
      <FeedRankingSection />

      {/* Adding a New Country Info Panel */}
      <div style={{
        background: '#F7F7F7',
        border: '1px solid #E0E0E0',
        borderRadius: '8px',
        padding: '20px',
        marginTop: '32px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          marginBottom: '16px'
        }}>
          <Info size={24} color={theme.primary} style={{ flexShrink: 0, marginTop: '2px' }} />
          <h3 style={{
            margin: 0,
            fontSize: '18px',
            fontWeight: '600',
            color: theme.text.primary
          }}>
            {tAdmin('info.adding_country_title', 'Adding a New Country')}
          </h3>
        </div>

        <p style={{
          margin: '0 0 16px 0',
          fontSize: '14px',
          color: theme.text.secondary,
          lineHeight: '1.6'
        }}>
          {tAdmin('info.adding_country_intro', 'Importing boundary data for a new country requires a developer. The process is:')}
        </p>

        <ol style={{
          margin: '0 0 20px 0',
          paddingLeft: '20px',
          fontSize: '14px',
          color: theme.text.primary,
          lineHeight: '1.8'
        }}>
          <li>
            {tAdmin('info.step1', 'Download GADM boundary data for the country from')}{' '}
            <a href="https://gadm.org/download_country.html" target="_blank" rel="noopener noreferrer" style={{ color: theme.primary }}>
              https://gadm.org/download_country.html
            </a>{' '}
            {tAdmin('info.step1b', '(use GeoJSON format)')}
          </li>
          <li>
            {tAdmin('info.step2', 'Run the import script against Supabase:')}{' '}
            <code style={{
              background: '#E5E7EB',
              padding: '2px 6px',
              borderRadius: '3px',
              fontSize: '13px',
              fontFamily: 'monospace'
            }}>
              node scripts/import_boundaries.js --country=XX
            </code>{' '}
            {tAdmin('info.step2b', '(where XX is the ISO 3166-1 country code)')}
          </li>
          <li>
            {tAdmin('info.step3', 'Return here to configure level labels, activation thresholds, and switch levels to active.')}
          </li>
        </ol>

        <div style={{
          borderTop: '1px solid #E0E0E0',
          paddingTop: '16px'
        }}>
          <h4 style={{
            margin: '0 0 12px 0',
            fontSize: '15px',
            fontWeight: '600',
            color: theme.text.primary
          }}>
            {tAdmin('info.imported_title', 'Countries currently imported')}:
          </h4>
          {importedCountries.length > 0 ? (
            <ul style={{
              margin: 0,
              paddingLeft: '20px',
              fontSize: '14px',
              color: theme.text.primary,
              lineHeight: '1.8'
            }}>
              {importedCountries.map(country => (
                <li key={country.code}>
                  <strong>{country.code}</strong> — {country.name} ({country.boundaries.toLocaleString(formattingLocale)} {tAdmin('info.boundaries', 'boundaries')}, {country.levels} {tAdmin('info.levels', 'levels')})
                </li>
              ))}
            </ul>
          ) : (
            <p style={{
              margin: 0,
              fontSize: '14px',
              color: theme.text.secondary,
              fontStyle: 'italic'
            }}>
              {tAdmin('info.no_countries', 'No countries imported yet.')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default LocationDepthAdmin;
